import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate, type Location } from 'react-router-dom';
import { PageActiveContext } from './lib/pageActive';
import { ACCT_PATH, acctKey, acctSubOfKey, DISPATCH_PATH, dispatchKey, dispatchSubOfKey, MASTER_PATH, masterKey, masterSubOfKey, PROFIT_PATH, profitKey, profitSubOfKey, clearTabs, closeTab, initFavs, isFav, moveTab, toggleFav, pathOfKey, saveScroll, savedScroll, touchTab, usePageTabs, type PageTab } from './lib/pageTabs';
import { auth, firebaseConfigured, signOutNow, STAFF_DOMAIN } from './lib/firebase';
import { useTheme, type Theme } from './lib/theme';
import ThemeButton from './components/ThemeButton';
import Login from './pages/Login';
import Home from './pages/Home';
import MasterData, { MASTER_TABS } from './pages/MasterData';
import MenuSearch, { type MenuSearchItem } from './components/MenuSearch';
import Dispatch, { DISPATCH_TABS } from './pages/Dispatch';
import Settle, { SETTLE_TABS } from './pages/Settle';
import ResultDaily from './pages/ResultDaily';
import ResultMo from './pages/ResultMo';
import ResultMoDef from './pages/ResultMoDef';
import ResultReq from './pages/ResultReq';
import Improve from './pages/Improve';
import Manual from './pages/Manual';
import { useManualNews } from './lib/manualNews';
import NoticeAdmin from './pages/NoticeAdmin';
import FinInst from './pages/FinInst';
import Admin from './pages/Admin';
import Accounting, { ACCT_TABS, isAcctTab } from './pages/Accounting';
import InvoiceStatus from './pages/InvoiceStatus';
import DriverApp from './driver/DriverApp';
import ErrorBoundary from './components/ErrorBoundary';
import { api, dropReadCaches } from './lib/api';
import { PageRefreshContext, onRefreshRequest, requestRefresh } from './lib/pageRefresh';
import { canScreen, clearMe, ROLE_LABEL, useMe, type Screen } from './lib/me';
import { clearSticky, peekSticky } from './lib/sticky';
import { resetWarmUp, warmUp } from './lib/warm';
import { clearStaff } from './lib/staff';
import { useMenuOrder } from './lib/menuOrder';
import Modal from './components/Modal';
import MenuOrderEditor from './components/MenuOrderEditor';
import LoadingOverlay from './components/LoadingOverlay';
import SettleScopeNote from './components/SettleScopeNote';
import OwnerPickSelect from './components/OwnerPickSelect';
import { setOwnerPick, useOwnerPick } from './lib/ownerPick';
import BrandLogo from './components/BrandLogo';
import InstallPrompt from './components/InstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import { InvoiceRequestAlerts, InvoiceRequestHeaderButton, useInvReqPulse } from './pages/InvoiceRequest';
import AllocChanges from './pages/AllocChanges';
import PostClose from './pages/PostClose';
import { PostCloseAlerts, usePostCloseCount } from './components/PostCloseAlerts';
import { CloseDeadlineAlert } from './components/CloseDeadlineAlert';
import { LatePayAlert } from './components/LatePay';
import Profit, { PROFIT_OPEN_TAB, PROFIT_TABS } from './pages/Profit';
import { AllocChangeAlerts, useAllocChangeCount } from './components/AllocChangeAlerts';

// screen 이 있는 메뉴는 화면 권한이 「숨김」이면 안 보인다(대시보드·개선요청은 전원).
const NAV: { to: string; label: string; end: boolean; screen?: Screen }[] = [
  { to: '/', label: '대시보드', end: true },
  { to: '/master', screen: 'master', label: '기준정보', end: false },
  // 운행편성 변경(10-02 way) — 구글폼 「차량변경정보공유」 대체. 보기는 기준정보 조회 권한이면 누구나(회계·정산 포함).
  { to: '/alloc-changes', screen: 'master', label: '편성 변경', end: false },
  { to: '/dispatch', screen: 'dispatch', label: '접수·배차', end: false },
  { to: '/settle', screen: 'settle', label: '정산', end: false },
  // 마감 후 수정 팀 승인(10-28 way) — 회계팀·인차팀·정산 조회·관리자에게(인차팀은 정산 화면 권한이 없을 수 있어 화면 권한으로 거르지 않는다 · 아래 filter).
  { to: '/post-close', label: '마감 후 수정', end: false },
  { to: '/result-daily', screen: 'result-daily', label: '재무추가', end: false },
  { to: '/result-mo', screen: 'result-mo', label: '월정기', end: false },
  { to: '/result-req', screen: 'result-req', label: '영업정산', end: false },
  { to: '/fin-inst', screen: 'fin-inst', label: '할부금', end: false },
  // 공지사항 — 기사앱 공지 쓰기·폰 알림(09-27). 기본은 배차 이상만(화면 권한으로 조정).
  { to: '/notices', screen: 'notices', label: '공지사항', end: false },
  // 개선요청 게시판 — 권한 구분 없이 전원에게 보인다(불편을 말할 자리는 막지 않는다).
  { to: '/improve', label: '개선요청', end: false },
  // 사용 설명서(10-01) — 전원. 본문은 배포에 같이 실려 나간다(`lib/manualGuide.json`).
  { to: '/manual', label: '사용 설명서', end: false },
  // 세금계산서 발행현황(개선요청 #54) — 영업이 정산월 청구액 ↔ 계산서를 본다. 정산을 볼 수 있는 사람에게(정산 범위 그대로).
  { to: '/invoice-status', screen: 'settle', label: '세금계산서 발행현황', end: false },
  // 경영 분석(10-24 way) — 월 수익·추세(시트 주요정산계정추세분석·수익TOOL 이관). 탭 대부분은 화면 권한 `profit`(기본 관리자만),
  // 「거래처 추세」 탭만 전 직원(10-27 way) → 메뉴는 전원에게 보이고 화면이 탭을 가른다(`pages/Profit.tsx`).
  { to: '/profit', label: '경영 분석', end: false },
];

const DRIVER_HOST = 'driver.busin.co.kr';

/**
 * 두 앱이 한 번들에 산다:
 *  - `/driver/*` → 기사앱(휴대폰 OTP·custom token, 사내 이메일 없음). 별도 인증 트리.
 *  - 그 밖 → 담당자 웹(Google 사내 계정).
 * 경로로 먼저 가른다 — 기사가 Google 로그인 화면을 볼 일이 없어야 한다.
 */
export default function App() {
  // 기사 전용 주소(driver.busin.co.kr, 10-06) — 주소만 쳐도 기사앱으로. 담당자 화면은 이 주소에서 열지 않는다.
  if (typeof window !== 'undefined' && window.location.hostname === DRIVER_HOST
    && !window.location.pathname.startsWith('/driver')) {
    window.location.replace(`/driver${window.location.search}${window.location.hash}`);
    return null;
  }
  // 새 배포 알림은 두 앱 공통(로그인 전에도) — `components/UpdatePrompt`.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/driver')) {
    return (
      <>
        <BrowserRouter basename="/driver">
          <ErrorBoundary variant="driver">
            <DriverApp />
          </ErrorBoundary>
        </BrowserRouter>
        <UpdatePrompt />
      </>
    );
  }
  return (
    <>
      <ErrorBoundary>
        <StaffApp />
      </ErrorBoundary>
      <UpdatePrompt />
    </>
  );
}

function StaffApp() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!firebaseConfigured) {
      setReady(true);
      return;
    }
    return onAuthStateChanged(auth, (u) => {
      setUser(u && u.email?.endsWith(STAFF_DOMAIN) ? u : null);
      setReady(true);
    });
  }, []);

  if (!ready) return <Splash text="불러오는 중…" />;
  if (!firebaseConfigured) return <NotConfigured />;
  if (!user) return <Login />;

  return (
    <BrowserRouter>
      <Shell user={user} />
    </BrowserRouter>
  );
}

/**
 * 로그인 이후 껍데기. 메뉴는 권한에 따라 달라지지만 **그건 편의일 뿐이고**,
 * 실제 차단은 서버의 `requireRole` 이 한다(주소를 직접 쳐도 API 가 막는다).
 */
function Shell({ user }: { user: User }) {
  const { me } = useMe();
  const { pathname } = useLocation();
  // 회계 자리는 재무추가 바로 뒤(개선요청 #11 1번: 정산 · 재무추가 · 회계 · 월정기 순).
  // 메뉴는 화면 권한(역할 + 팀·개인 예외, 서버 계산)이 「숨김」이 아닐 때만 — 회계는 기본이 회계팀·관리자만.
  const withAcct = NAV.flatMap((n) =>
    n.to === '/result-daily' ? [n, { to: '/accounting', label: '회계', end: false, screen: 'accounting' as Screen }] : [n],
  ).filter((n) => (n.to === '/post-close'
    ? me?.role === 'admin' || me?.team === '회계팀' || me?.team === '인차팀' || canScreen(me, 'settle', 'read')
    : !n.screen || canScreen(me, n.screen, 'read')));
  const navDefault = me?.role === 'admin' ? [...withAcct, { to: '/admin', label: '사용자', end: false }] : withAcct;
  // 메뉴 순서는 계정마다 바꿔 둘 수 있다(게시판 #160) — 보이는 메뉴는 그대로 권한이 정하고 순서만 바뀐다.
  const [nav, , setNavOrder] = useMenuOrder('nav.order', navDefault, (n) => n.to);
  const [orderOpen, setOrderOpen] = useState(false);
  // 화면 찾기(#396) — 보이는 메뉴(권한으로 거른 nav)와 그 세부 탭만. 못 여는 화면은 결과에 안 나온다.
  const searchItems = useMemo<MenuSearchItem[]>(() => {
    const has = (to: string) => nav.some((n) => n.to === to);
    const sub = (to: string, head: string, tabs: readonly (readonly [string, string])[]) =>
      has(to) ? tabs.map(([id, l]) => ({ label: `${head} › ${l}`, to: `${to}?tab=${id}` })) : [];
    return [
      ...nav.map((n) => ({ label: n.label, to: n.to })),
      ...sub('/master', '기준정보', MASTER_TABS),
      ...sub('/dispatch', '접수·배차', DISPATCH_TABS),
      ...sub('/settle', '정산', SETTLE_TABS),
      ...sub('/accounting', '회계', ACCT_TABS),
      ...sub('/profit', '경영 분석', canScreen(me, 'profit', 'read') ? PROFIT_TABS : PROFIT_TABS.filter(([id]) => id === PROFIT_OPEN_TAB)),
      ...(has('/result-mo') ? [{ label: '월정기 › 정기항목', to: '/result-mo-def' }] : []),
    ];
  }, [nav, me]);

  /**
   * 개선요청 배지 — 미처리 건수와 «내 글에 새 답변». 게시판은 안 들어가면 안 보이므로
   * 메뉴에 숫자가 없으면 올린 사람만 기다리다 잊는다(다른 프로젝트 넷 다 배지를 붙였다).
   * ⚠️ 목록 전체가 아니라 숫자만 세는 전용 호출이다 — 메뉴는 모든 화면에 떠 있다.
   */
  const [board, setBoard] = useState<{ open: number; mineReplied: number } | null>(null);
  // 세금계산서 발행 요청 — 회계 메뉴 배지(새 요청 + 영업 답 옴). 값은 `InvoiceRequestAlerts` 가 45초마다 맞춘다.
  const invreq = useInvReqPulse();
  const invreqBadge = invreq?.acct ? invreq.acct.fresh.length + invreq.acct.replied.length : 0;
  // 편성 변경 — 내가 아직 「확인」 안 한 것(정산 담당·회계). 값은 `AllocChangeAlerts` 가 1분마다 맞춘다.
  const allocBadge = useAllocChangeCount();
  // 마감 후 수정 — 내가 지금 승인할 수 있는 기안 수(`PostCloseAlerts` 가 1분마다 맞춘다).
  const postCloseBadge = usePostCloseCount();
  // 🔴 로그인 때 한 번만 세면 그 뒤 처리돼도 숫자가 그대로 남는다(2026-09-28 way 「모두 확인했는데 4는?」 —
  // 실제 미처리 0건인데 아침에 센 4가 떠 있었다). 메뉴를 옮길 때·창으로 돌아올 때·3분마다 다시 센다.
  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      api
        .countImprovements()
        .then((r) => !cancelled && setBoard(r))
        .catch(() => {
          /* 배지는 없어도 그만 — 게시판이 막혀도 업무 화면은 그대로 쓴다. */
        });
    refresh();
    const timer = window.setInterval(refresh, 3 * 60_000);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [pathname]);

  // 로그인 직후 목록 함수들을 한 번에 깨운다. 사람이 대시보드를 읽는 동안 끝난다(`lib/warm.ts`).
  useEffect(() => {
    warmUp();
  }, []);

  // 폰에서 메뉴 줄은 가로로 밀어서 본다(#85) — 화면을 옮기면 **지금 메뉴가 보이게** 끌어 온다(뒤쪽 메뉴가 줄 밖에 숨지 않게).
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const active = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (active) el.scrollTo({ left: active.offsetLeft - el.clientWidth / 2 + active.offsetWidth / 2 });
  }, [pathname]);
  // 한 줄 메뉴(2xl)도 넘치면 그 줄 안에서 민다(10-29 way 「가로 스크롤바 난잡」 — 넘친 메뉴가 페이지 전체 가로 스크롤을 만들었다).
  // 막대는 평소 숨김(`scroll-quiet`)이라 휠을 가로로 돌려 준다.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // 위에 고정된 메뉴+화면 탭 높이를 `--top-bar` 로 알린다 — 본문에서 sticky 로 붙는 칸(배차 왼쪽 접수 목록)이
  // 메뉴 밑에 숨지 않게 그 아래에 붙는다. 메뉴 줄 수(1~2줄)·창 폭에 따라 높이가 바뀌어 잰다.
  const topBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--top-bar', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 화면 색(#381) — 밝게·부드럽게·어둡게. 계정에 기억하고 <html data-theme> 로 바꾼다(`lib/theme.ts`).
  const [theme, setTheme] = useTheme();

  async function logout() {
    clearMe(); // 다음 사람이 앞사람 권한을 물고 시작하지 않게
    clearStaff();
    clearSticky(); // 앞사람이 보던 조건도
    setOwnerPick(null); // 머리줄 「담당자」도
    clearTabs(); // 앞사람이 열어 둔 화면도
    resetWarmUp();
    await signOutNow();
  }

  // 폰 서랍 메뉴 — 화면을 옮기면 닫는다.
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [pathname]);
  // 사용 설명서 — 마지막으로 연 뒤 반영된 개선요청 수(`lib/manualNews.ts`).
  const manualNews = useManualNews();
  const badgeOf = (to: string) =>
    to === '/accounting' ? invreqBadge : to === '/alloc-changes' ? allocBadge : to === '/post-close' ? postCloseBadge : to === '/manual' ? manualNews : to === '/improve' && board ? board.mineReplied || board.open : 0;
  const tabLabel = (key: string) =>
    acctSubOfKey(key) ? `회계 › ${ACCT_TABS.find(([id]) => id === acctSubOfKey(key))?.[1] ?? ''}` :
    dispatchSubOfKey(key) ? `접수·배차 › ${DISPATCH_TABS.find(([id]) => id === dispatchSubOfKey(key))?.[1] ?? ''}` :
    masterSubOfKey(key) ? `기준정보 › ${MASTER_TABS.find(([id]) => id === masterSubOfKey(key))?.[1] ?? ''}` :
    profitSubOfKey(key) ? `경영 분석 › ${PROFIT_TABS.find(([id]) => id === profitSubOfKey(key))?.[1] ?? ''}` :
    key === '/result-mo-def' ? '월정기 › 정기항목' : key === '/admin' ? '사용자' : nav.find((n) => n.to === key)?.label ?? '버스인';
  const current =
    pathname === '/result-mo-def' ? '월정기 › 정기항목' : nav.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label ?? '버스인';

  return (
    <div className="flex min-h-full flex-col">
      {/* 조회가 나가 있으면 화면 가운데 카드로 알린다 — 전 화면 공통(`lib/loading.ts`). */}
      <LoadingOverlay />
      {/* 폰(md 미만): 윗줄 = 메뉴 버튼 + **한 줄 제목**, 아래 = 탭바, 전체 메뉴 = 왼쪽 서랍(YDYOPS·DYOPS 방식, 2026-09-30 way).
          PC 머리글(아래 header)은 md 이상에서만 보인다 — 폰에서 메뉴 13개를 가로로 미는 방식(#85)은 뒤쪽 메뉴를 찾기 어려웠다. */}
      <MobileTop title={current} onMenu={() => setDrawer(true)} />
      <MobileDrawer open={drawer} onClose={() => setDrawer(false)} nav={nav} pathname={pathname} searchItems={searchItems}
                    badgeOf={badgeOf} email={user.email ?? ''} roleLabel={me ? ROLE_LABEL[me.role] : ''} onLogout={logout}
                    theme={theme} onTheme={setTheme}
                    onOrder={() => { setDrawer(false); setOrderOpen(true); }} />
      {orderOpen && (
        <Modal width="max-w-md">
          <MenuOrderEditor title="메뉴 순서" items={nav.map((n) => ({ id: n.to, label: n.label }))}
                           onSave={setNavOrder} onReset={() => setNavOrder(null)} onClose={() => setOrderOpen(false)} />
        </Modal>
      )}
      <MobileTabBar nav={nav} pathname={pathname} badgeOf={badgeOf} onMore={() => setDrawer(true)} />
      {/* 윗줄 메뉴는 **화면 위에 고정**(2026-09-29 게시판 #44 — 스크롤을 내리면 메뉴가 사라졌다).
          z-40 = 표 머리글(z-10~20, 각자 스크롤 상자 안)보다 위 · 팝업(z-50)보다 아래. */}
      {/* 폰·좁은 창(xl 미만, 게시판 #85): 메뉴가 칸을 못 얻으면 **글자 하나씩 세로로 쪼개지고** 그 폭 때문에
          페이지가 화면보다 넓어져 본문이 작게 보였다 → 메뉴를 아랫줄로 내리고 **가로로 밀어서** 본다(글자는 한 줄 고정).
          2xl(1536) 이상만 한 줄 — 09-30 way 「메뉴 글자 두 줄 금지」로 1280~1535 도 아랫줄 가로 밀기로 바꿨다(예전엔 xl 부터 한 줄이라 「대시보/드」처럼 글자가 접혔다). */}
      <div ref={topBarRef} className="sticky top-0 z-40 hidden md:block">
      <header className="flex flex-wrap items-center 2xl:flex-nowrap gap-x-6 gap-y-1 border-b border-line bg-surface px-3 py-1.5 sm:px-6">
        <NavLink to="/" end aria-label="버스인 처음 화면" className="shrink-0 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <BrandLogo />
        </NavLink>
        <nav ref={navRef} className="relative order-last -mx-3 flex w-[calc(100%+1.5rem)] gap-1 overflow-x-auto px-3 pb-0.5 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-6 scroll-quiet 2xl:order-none 2xl:mx-0 2xl:w-auto 2xl:min-w-0 2xl:flex-1 2xl:px-0 2xl:pb-0">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `shrink-0 whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium ${
                  // 정기항목(/result-mo-def)은 월정기의 하위 화면 — 메뉴가 없으니 월정기에 불을 켠다.
                  isActive || (n.to === '/result-mo' && pathname === '/result-mo-def')
                    ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
                }`
              }
            >
              {n.label}
              {n.to === '/accounting' && invreqBadge > 0 && (
                <span className="ml-1.5 animate-pulse rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white"
                      title={`확인 안 된 세금계산서 발행 요청·답변 ${invreqBadge}건`}>
                  {invreqBadge}
                </span>
              )}
              {n.to === '/post-close' && postCloseBadge > 0 && (
                <span className="ml-1.5 animate-pulse rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white"
                      title={`승인할 마감 후 수정 기안 ${postCloseBadge}건`}>
                  {postCloseBadge}
                </span>
              )}
              {n.to === '/alloc-changes' && allocBadge > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white"
                      title={`확인 안 한 운행편성 변경 ${allocBadge}건`}>
                  {allocBadge}
                </span>
              )}
              {n.to === '/improve' && board && board.open + board.mineReplied > 0 && (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white ${
                    board.mineReplied > 0 ? 'bg-red-500' : 'bg-ink-faint'
                  }`}
                  title={
                    board.mineReplied > 0
                      ? `내 요청에 새 답변 ${board.mineReplied}건 · 미처리 ${board.open}건`
                      : `미처리 ${board.open}건`
                  }
                >
                  {board.mineReplied > 0 ? board.mineReplied : board.open}
                </span>
              )}
            </NavLink>
          ))}
          <button type="button" onClick={() => setOrderOpen(true)} title="메뉴 순서 바꾸기" aria-label="메뉴 순서 바꾸기"
                  className="shrink-0 rounded-lg px-2 py-1 text-sm text-ink-faint hover:bg-canvas hover:text-ink-soft">
            ⇅
          </button>
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2 text-sm sm:gap-3">
          <OwnerPickSelect />
          <InvoiceRequestHeaderButton />
          {/* 이메일은 넓은 화면에서만 — 1440 에서 「계산서 요청」 버튼이 들어오면 메뉴가 두 줄로 접힌다(2026-09-28 실측).
              역할 글자는 폰에선 뺀다 — 로고·계산서 요청·로그아웃이 한 줄에 들어가게. */}
          <span className="hidden whitespace-nowrap text-ink-soft sm:inline" title={user.email ?? ''}>
            <span className="hidden min-[1800px]:inline">{user.email}</span>
            {me && <span className="ml-2 text-ink-faint">{ROLE_LABEL[me.role]}</span>}
          </span>
          <ThemeButton theme={theme} onChange={setTheme} compact />
          <button className="btn-ghost whitespace-nowrap" onClick={logout}>
            로그아웃
          </button>
        </div>
      </header>
      {/* 열린 화면 탭(#168) — 머리글과 같이 위에 고정 */}
      {/* 화면 찾기(#396)는 탭 줄 오른쪽 끝 — 머리글은 2xl 한 줄 메뉴에 남는 자리가 없다(넣었더니 192px 밀려남 · 10-23 실측). */}
      <PageTabStrip labelOf={tabLabel} right={<MenuSearch items={searchItems} className="w-60" />} />
      </div>

      {/*
        본문은 **화면 폭을 그대로 쓴다**(2026-09-16 사용자 요청 — PC 기준 꽉 채워서).
        예전엔 `mx-auto max-w-7xl`(1,280px)이라 1920 화면에서 좌우 640px 이 비었고,
        그 좁은 칸에 칼럼 10개짜리 표를 밀어 넣느라 오른쪽이 잘렸다(issues 115 와 한 몸).
        ⚠️ 머리글(`header`)도 `px-3 sm:px-6` 이라 좌우 여백이 맞물린다 — 한쪽만 바꾸면 어긋난다(메뉴 줄의 음수 여백도 같은 값).
        ⚠️ 팝업(`Modal`)의 `max-w-*` 는 별개다. 폼은 넓다고 읽기 쉬워지지 않는다.
        위아래 여백·머리글 높이는 **최소로**(2026-09-26 사용자 요청 — 상단을 줄여 표가 더 보이게).
      */}
      {/* 폰은 아래 탭바만큼 바닥을 띄운다(`--bottom-bar`, PC 는 0). `app-main` = 폰 전용 CSS 가 거는 자리(globals.css). */}
      <main className="app-main w-full min-w-0 flex-1 px-3 py-2 pb-[calc(var(--bottom-bar)+0.5rem)] sm:px-6 md:py-3">
        <SettleScopeNote />
        {/* 세금계산서 발행 요청 환기(팝업·빨간 띠·탭 제목) — 어느 화면에 있든 */}
        <InvoiceRequestAlerts />
        {/* 운행편성 변경 — 회계팀 팝업 · 메뉴 배지(1분마다) */}
        <AllocChangeAlerts />
        {/* 마감 후 수정 — 회계팀·인차팀 승인 요청 팝업(1분마다) */}
        <PostCloseAlerts />
        {/* 팀별 마감일 공지 — 기한 당일 13시·15시(11-04 way) */}
        <CloseDeadlineAlert />
        {/* 마감 후 지급 대기 — 인사팀(월정기 입력) 팝업(11-05 way · 10분마다) */}
        <LatePayAlert />
        {/* 앱 설치 안내(하단 배너) — 이미 앱으로 열었거나 닫았으면 안 뜬다 */}
        <InstallPrompt />
        <Pages />
      </main>
    </div>
  );
}

/**
 * 화면 본문 — **껍데기가 다시 그려져도 따라 그려지지 않게** memo 로 떼어 둔다(2026-09-30 속도 4차).
 * 껍데기는 배지 때문에 45초(계산서 요청)·3분·창 복귀(개선요청)마다 다시 그려지는데, 본문이 안에 있으면
 * 그때마다 출납일보 3,000줄 같은 표가 통째로 다시 그려져 **손대지 않아도 0.3~2.4초씩 멈췄다**(logPerf 09-29 실측).
 * 경로가 바뀌면 `Routes` 가 스스로 다시 그린다(location 은 context 로 받는다). 여기에 props 를 넘기지 말 것 — 넘기면 memo 가 풀린다.
 */
const PAGES: Record<string, () => JSX.Element> = {
  '/': Home,
  '/master': MasterData,
  '/alloc-changes': AllocChanges,
  '/post-close': PostClose,
  '/dispatch': Dispatch,
  '/settle': Settle,
  '/result-daily': ResultDaily,
  '/result-mo': ResultMo,
  // 정기항목(템플릿) 관리. 상단 메뉴가 아니라 월정기 화면에서 들어간다 — 레거시도 메뉴가 하나다.
  '/result-mo-def': ResultMoDef,
  '/result-req': ResultReq,
  '/fin-inst': FinInst,
  '/improve': Improve,
  '/manual': Manual,
  '/notices': NoticeAdmin,
  '/accounting': Accounting,
  '/invoice-status': InvoiceStatus,
  '/profit': Profit,
  '/admin': Admin,
};

/**
 * 화면 탭 키 — 주소 경로. 회계만 세부 탭까지(`/accounting:cash` · #211). `?req=번호`(발행 요청 링크)는 발행 요청 탭.
 * 세부 탭 없는 `/accounting` 은 null → Pages 가 마지막 회계 탭으로 보낸다.
 */
function pageKeyOf(loc: Location): string | null {
  if (loc.pathname === ACCT_PATH) {
    const p = new URLSearchParams(loc.search);
    const sub = p.get('tab');
    if (isAcctTab(sub)) return acctKey(sub);
    return p.get('req') ? acctKey('invreq') : null;
  }
  if (loc.pathname === DISPATCH_PATH) {
    const sub = new URLSearchParams(loc.search).get('tab');
    return sub && DISPATCH_TABS.some(([id]) => id === sub) ? dispatchKey(sub) : null;
  }
  if (loc.pathname === MASTER_PATH) {
    const sub = new URLSearchParams(loc.search).get('tab');
    return sub && MASTER_TABS.some(([id]) => id === sub) ? masterKey(sub) : null;
  }
  if (loc.pathname === PROFIT_PATH) {
    const sub = new URLSearchParams(loc.search).get('tab');
    return sub && PROFIT_TABS.some(([id]) => id === sub) ? profitKey(sub) : null;
  }
  return PAGES[loc.pathname] ? loc.pathname : null;
}

/**
 * 🔴 **화면 탭(게시판 #168, 10-09)** — 열어 본 화면은 **숨겨 둔 채 살려 둔다**(레거시 MDI 탭).
 * 재무추가에 입력하다 기준정보에서 차량을 찾고 돌아와도 입력·조건·스크롤이 그대로다. 탭을 닫으면 처음부터.
 * - 탭마다 자기 주소를 `<Routes location>` 으로 붙든다 — 숨은 화면이 남의 `?tab=`·`?q=` 를 읽지 않는다.
 * - 숨은 화면은 `PageActiveContext=false` — 팝업(Modal)·창 전체 키보드가 비켜선다(`lib/pageActive.ts`).
 * - 표 높이 계산(뷰포트에 묶기)은 숨은 동안 0 을 재므로 보일 때 `resize` 를 한 번 쏴 다시 재게 한다.
 */
const Pages = memo(function Pages() {
  const location = useLocation();
  const tabs = usePageTabs();
  const { me } = useMe();
  const key = pageKeyOf(location);

  useLayoutEffect(() => {
    if (key) touchTab(key, location);
  }, [key, location]);

  // 스크롤 — 창 하나를 같이 쓰므로 탭마다 적어 두고 돌아올 때 되돌린다.
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => {
    const on = () => { if (keyRef.current) saveScroll(keyRef.current, window.scrollY); };
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  useLayoutEffect(() => {
    if (!key) return;
    window.scrollTo(0, savedScroll(key));
    window.dispatchEvent(new Event('resize'));
  }, [key]);

  // 회계를 세부 탭 없이 열면(상단 메뉴) — 열려 있는 회계 탭 중 마지막에 본 것, 없으면 기억된 탭으로(#211).
  if (location.pathname === ACCT_PATH && !key) {
    const open = tabs.filter((t) => acctSubOfKey(t.key)).sort((a, b) => b.seen - a.seen)[0];
    if (open) return <Navigate to={`${open.loc.pathname}${open.loc.search}${open.loc.hash}`} replace />;
    const last = peekSticky('acct.tab') ?? me?.prefs?.['acct.tab'];
    return <Navigate to={`${ACCT_PATH}?tab=${isAcctTab(last) ? last : 'tx'}`} replace />;
  }
  // 접수·배차를 세부 탭 없이 열면(상단 메뉴·?q= 링크) — 마지막에 본 접수·배차 탭으로. 다른 조건(?q=)은 그대로 들고 간다.
  if (location.pathname === DISPATCH_PATH && !key) {
    const open = tabs.filter((t) => dispatchSubOfKey(t.key)).sort((a, b) => b.seen - a.seen)[0];
    const p = new URLSearchParams(location.search);
    if (open && !p.toString()) return <Navigate to={`${open.loc.pathname}${open.loc.search}`} replace />;
    const last = peekSticky('dispatch.tab');
    p.set('tab', open ? dispatchSubOfKey(open.key)! : typeof last === 'string' && DISPATCH_TABS.some(([id]) => id === last) ? last : 'recv');
    return <Navigate to={`${DISPATCH_PATH}?${p.toString()}`} replace />;
  }
  // 기준정보를 세부 탭 없이 열면(상단 메뉴) — 열려 있는 기준정보 탭 중 마지막에 본 것, 없으면 기억된 탭(처음엔 소속)으로.
  if (location.pathname === MASTER_PATH && !key) {
    const open = tabs.filter((t) => masterSubOfKey(t.key)).sort((a, b) => b.seen - a.seen)[0];
    if (open) return <Navigate to={`${open.loc.pathname}${open.loc.search}`} replace />;
    const last = peekSticky('master.tab');
    return <Navigate to={`${MASTER_PATH}?tab=${typeof last === 'string' && MASTER_TABS.some(([id]) => id === last) ? last : MASTER_TABS[0]![0]}`} replace />;
  }
  // 경영 분석을 세부 탭 없이 열면(상단 메뉴) — 열려 있는 경영 분석 탭 중 마지막에 본 것, 없으면 기억된 탭으로.
  // 화면 권한 `profit` 이 없으면 「거래처 추세」 하나뿐 — 다른 주소로 와도 그 탭으로(화면 탭 이름이 엉뚱하게 남지 않게).
  // 권한을 모르는 동안(로그인 직후)엔 홈으로 튕기지 않고 기다린다.
  if (location.pathname === PROFIT_PATH && !me && !key) return null;
  if (location.pathname === PROFIT_PATH && me) {
    const sub = key ? profitSubOfKey(key) : null;
    if (!canScreen(me, 'profit', 'read') && sub !== PROFIT_OPEN_TAB) return <Navigate to={`${PROFIT_PATH}?tab=${PROFIT_OPEN_TAB}`} replace />;
    if (!key) {
      const open = tabs.filter((t) => profitSubOfKey(t.key)).sort((a, b) => b.seen - a.seen)[0];
      if (open) return <Navigate to={`${open.loc.pathname}${open.loc.search}`} replace />;
      const last = peekSticky('profit.tab');
      return <Navigate to={`${PROFIT_PATH}?tab=${typeof last === 'string' && PROFIT_TABS.some(([id]) => id === last) ? last : PROFIT_TABS[0]![0]}`} replace />;
    }
  }
  if (!key) return <Navigate to="/" replace />;
  const list = tabs.some((t) => t.key === key) ? tabs : [...tabs, { key, loc: location, seen: 0 }];
  return (
    <>
      {/* 새로고침·별표로 되살린 탭(seen 0)은 누를 때 연다 — 한꺼번에 조회가 몰리지 않게. */}
      {list.filter((t) => t.key === key || t.seen > 0).map((t) => (
        <KeptPage key={t.key} pageKey={t.key} path={pathOfKey(t.key)} loc={t.key === key ? location : t.loc} active={t.key === key} />
      ))}
    </>
  );
});

const KeptPage = memo(function KeptPage({ pageKey, path, loc, active }: { pageKey: string; path: string; loc: Location; active: boolean }) {
  const El = PAGES[path] ?? Home;
  // 「↻ 새로고침」(lib/pageRefresh.ts) — 공용 목록이 있으면 같은 조건으로 다시 읽고, 없으면 화면을 다시 연다.
  const [tick, setTick] = useState(0);
  const [remount, setRemount] = useState(0);
  const claims = useRef(0);
  const claim = useCallback(() => {
    claims.current += 1;
    return () => { claims.current -= 1; };
  }, []);
  const refresh = useMemo(() => ({ tick, claim, request: () => requestRefresh(pageKey) }), [tick, claim, pageKey]);
  useEffect(() => onRefreshRequest(pageKey, () => {
    dropReadCaches();
    if (claims.current > 0) setTick((n) => n + 1);
    else setRemount((n) => n + 1);
  }), [pageKey]);
  // 머리줄 「담당자」가 바뀌면 — 공용 목록은 스스로 다시 읽고(useList), 그 훅이 없는 화면은 다시 연다(11-02).
  const ownerPick = useOwnerPick();
  const seenPick = useRef(ownerPick);
  useEffect(() => {
    if (seenPick.current === ownerPick) return;
    seenPick.current = ownerPick;
    if (claims.current === 0) setRemount((n) => n + 1);
  }, [ownerPick]);
  return (
    <div hidden={!active}>
      <PageActiveContext.Provider value={active}>
        <PageRefreshContext.Provider value={refresh}>
          <ErrorBoundary key={remount}>
            <Routes location={loc}>
              <Route path={path} element={<El />} />
            </Routes>
          </ErrorBoundary>
        </PageRefreshContext.Provider>
      </PageActiveContext.Provider>
    </div>
  );
});

/** 열린 화면 탭 줄(PC) — 누르면 그 화면으로(입력 그대로) · × 로 닫으면 다음에 처음부터. */
function PageTabStrip({ labelOf, right }: { labelOf: (key: string) => string; right?: ReactNode }) {
  const tabs = usePageTabs();
  const location = useLocation();
  const curKey = pageKeyOf(location);
  const navigate = useNavigate();
  const { me } = useMe();
  // ★ 즐겨찾기(#222) — 로그인한 사람의 설정을 한 번 읽어 그 탭들을 맨 앞에 띄운다. 바꾸면 계정 설정에 쓴다.
  const favInit = useRef<string | null>(null);
  useEffect(() => {
    if (!me || favInit.current === me.email) return;
    favInit.current = me.email;
    initFavs(me.prefs?.['page.favs'], (keys) => { void api.setMyPref({ key: 'page.favs', value: keys }).catch(() => undefined); });
  }, [me]);
  const go = (t: PageTab) => navigate(`${t.loc.pathname}${t.loc.search}${t.loc.hash}`);
  const close = (t: PageTab) => {
    if (isFav(t.key)) return; // 별표 탭은 닫지 않는다 — 별을 끄면 닫을 수 있다.
    const next = closeTab(t.key);
    if (t.key !== curKey) return;
    if (next) go(next); else navigate('/');
  };
  // #211 탭 끌어서 옮기기 — 놓는 탭의 왼쪽 반이면 앞, 오른쪽 반이면 뒤.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const favOnAt = useRef(0);
  const [dropAt, setDropAt] = useState<{ key: string; after: boolean } | null>(null);
  const endDrag = () => { setDragKey(null); setDropAt(null); };
  return (
    // 오른쪽 끝 = 화면 찾기(#396). 탭 목록만 가로로 밀리게 따로 묶는다 — 바깥이 overflow 면 찾기 결과 목록이 잘린다.
    <div className="flex items-end border-b border-line bg-canvas px-3 sm:px-6">
    <div className="flex min-w-0 flex-1 items-end gap-0.5 overflow-x-auto pt-1" role="tablist" aria-label="열린 화면">
      {tabs.map((t) => {
        const on = t.key === curKey;
        const fav = isFav(t.key);
        const mark = dropAt?.key === t.key && dragKey && dragKey !== t.key ? (dropAt.after ? 'shadow-[inset_-2px_0_0_0_rgb(var(--brand-600))]' : 'shadow-[inset_2px_0_0_0_rgb(var(--brand-600))]') : '';
        return (
          <div key={t.key} role="tab" aria-selected={on} draggable title="끌어서 순서 바꾸기"
               // #442 정여진 「탭이 눈에 잘 안 띈다」 — 탭마다 네모 상자 · 지금 화면은 위 굵은 파란 선 + 굵은 글씨.
               className={`group flex shrink-0 items-center rounded-t-md border border-b-0 text-[13px] ${mark} ${dragKey === t.key ? 'opacity-50' : ''} ${
                 on ? 'border-brand-500 border-t-[3px] bg-surface font-bold text-brand-700 shadow-sm'
                    : 'border-slate-300 bg-surface/70 font-medium text-ink hover:bg-brand-50'}`}
               onDragStart={(e) => { setDragKey(t.key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', t.key); }}
               onDragOver={(e) => {
                 if (!dragKey) return;
                 e.preventDefault();
                 const r = e.currentTarget.getBoundingClientRect();
                 const after = e.clientX > r.left + r.width / 2;
                 if (dropAt?.key !== t.key || dropAt.after !== after) setDropAt({ key: t.key, after });
               }}
               onDrop={(e) => { e.preventDefault(); if (dragKey && dropAt) moveTab(dragKey, dropAt.key, dropAt.after); endDrag(); }}
               onDragEnd={endDrag}
               onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); close(t); } }}>
            <button type="button" aria-label={fav ? `${labelOf(t.key)} 즐겨찾기 끄기` : `${labelOf(t.key)} 즐겨찾기`}
                    title={fav ? '두 번 누르면 즐겨찾기 끄기' : '즐겨찾기 — 껐다 켜도 열려 있고 맨 왼쪽에 고정됩니다'}
                    className={`ml-1.5 grid h-5 w-5 place-items-center rounded ${fav ? 'text-amber-500' : `text-ink-faint hover:text-amber-500 ${on ? '' : 'opacity-0 group-hover:opacity-100'}`}`}
                    // #442 — 잘못 눌러 별이 풀리지 않게, 켜기는 한 번 · 끄기는 두 번 누를 때만.
                    // 꺼진 별을 두 번 누르면 첫 누름이 켠 직후 두 번째가 끄지 않게 — 방금 켠 별은 두 번 누르기를 무시한다.
                    onClick={() => { if (!fav) { favOnAt.current = Date.now(); toggleFav(t.key); } }}
                    onDoubleClick={() => { if (fav && Date.now() - favOnAt.current > 800) toggleFav(t.key); }}>
              {fav ? '★' : '☆'}
            </button>
            <button type="button" className="whitespace-nowrap py-1 pl-1 pr-1" onClick={() => go(t)}>{labelOf(t.key)}</button>
            {on && (
              <button type="button" aria-label={`${labelOf(t.key)} 새로고침`}
                      title="새로고침 — 이 화면만 최신 내용으로 다시 읽습니다(고른 조건은 그대로)"
                      className="grid h-5 w-5 place-items-center rounded text-ink-soft hover:bg-canvas hover:text-brand-700"
                      onClick={() => requestRefresh(t.key)}>
                ↻
              </button>
            )}
            {/* #503 김희정 「별을 켜면 해제가 안 된다」 — 별표 탭에도 × 를 두고, 누르면 별을 풀고 닫는다.
                별 자체는 #442 대로 두 번 눌러야 꺼진다(잘못 눌러 풀리지 않게). 가운데 버튼 닫기는 별표 탭엔 여전히 안 먹는다. */}
            <button type="button" aria-label={fav ? `${labelOf(t.key)} 즐겨찾기 해제하고 닫기` : `${labelOf(t.key)} 닫기`}
                    title={fav ? '즐겨찾기 해제하고 닫기' : '닫기 — 다음에 열면 처음부터'}
                    className={`mr-1 grid h-5 w-5 place-items-center rounded text-ink-faint hover:bg-canvas hover:text-ink ${on ? '' : 'opacity-0 group-hover:opacity-100'}`}
                    onClick={() => { if (fav) toggleFav(t.key); close(t); }}>
              ×
            </button>
          </div>
        );
      })}
    </div>
    {right && <div className="shrink-0 py-1 pl-2">{right}</div>}
    </div>
  );
}

type NavItem = { to: string; label: string; end: boolean; screen?: Screen };
const isOn = (n: NavItem, pathname: string) =>
  (n.end ? pathname === n.to : pathname.startsWith(n.to)) || (n.to === '/result-mo' && pathname === '/result-mo-def');

/** 폰 윗줄 — 메뉴 버튼 + 지금 화면 이름(**한 줄**, 넘치면 …) + 계산서 요청. md 이상에선 안 보인다. */
function MobileTop({ title, onMenu }: { title: string; onMenu: () => void }) {
  return (
    <header className="sticky top-0 z-40 flex h-12 items-center gap-2 border-b border-line bg-surface/95 px-2 backdrop-blur md:hidden">
      <button type="button" onClick={onMenu} aria-label="메뉴 열기"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-soft active:bg-canvas">
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <div className="min-w-0 flex-1 truncate whitespace-nowrap text-base font-bold">{title}</div>
      <div className="shrink-0 [&_button]:px-2 [&_button]:text-xs">
        <InvoiceRequestHeaderButton />
      </div>
    </header>
  );
}

/** 폰 서랍 — 전체 메뉴(권한에 맞게 걸러진 그대로) · 계정 · 로그아웃. 여백을 누르거나 메뉴를 고르면 닫힌다. */
function MobileDrawer({ open, onClose, nav, pathname, searchItems, badgeOf, email, roleLabel, onLogout, onOrder, theme, onTheme }: {
  open: boolean; onClose: () => void; nav: NavItem[]; pathname: string; searchItems: MenuSearchItem[]; badgeOf: (to: string) => number;
  email: string; roleLabel: string; onLogout: () => void; onOrder: () => void; theme: Theme; onTheme: (t: Theme) => void;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return (
    <div className={`md:hidden ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
      <div onClick={onClose}
           className={`fixed inset-0 z-[45] bg-black/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} />
      <aside className={`fixed inset-y-0 left-0 z-[46] flex w-[80%] max-w-[18rem] flex-col bg-surface shadow-xl transition-transform duration-200 ${
               open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <BrandLogo />
          <button type="button" onClick={onClose} aria-label="메뉴 닫기" className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft active:bg-canvas">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <div className="border-b border-line p-2">
          <MenuSearch items={searchItems} onGo={onClose} />
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {nav.map((n) => {
            const b = badgeOf(n.to);
            return (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={onClose}
                       className={`flex items-center justify-between whitespace-nowrap rounded-lg px-3 py-2.5 text-[15px] font-medium ${
                         isOn(n, pathname) ? 'bg-brand-50 text-brand-700' : 'text-ink active:bg-canvas'}`}>
                <span className="truncate">{n.label}</span>
                {b > 0 && <span className="ml-2 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{b}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-line p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] text-sm">
          <div className="truncate text-ink-soft">{email}</div>
          {roleLabel && <div className="text-xs text-ink-faint">{roleLabel}</div>}
          <button className="btn-ghost mt-2 w-full" onClick={onOrder}>메뉴 순서 바꾸기</button>
          <ThemeButton theme={theme} onChange={onTheme} className="mt-1 w-full" />
          <button className="btn-ghost mt-1 w-full" onClick={onLogout}>로그아웃</button>
        </div>
      </aside>
    </div>
  );
}

/**
 * 폰 아래 탭바 — 자주 쓰는 4개 + 더보기(서랍). 권한으로 숨은 메뉴는 건너뛰고 다음 후보로 채운다.
 * 순서 = 정산 업무 동선(2026-09-30 way 「모바일로도 정산 충분히 가능하도록」).
 */
const TAB_PICK = ['/', '/settle', '/result-daily', '/dispatch', '/accounting', '/master', '/improve'];
function MobileTabBar({ nav, pathname, badgeOf, onMore }: {
  nav: NavItem[]; pathname: string; badgeOf: (to: string) => number; onMore: () => void;
}) {
  const tabs = TAB_PICK.map((to) => nav.find((n) => n.to === to)).filter((n): n is NavItem => !!n).slice(0, 4);
  const inTabs = tabs.some((t) => isOn(t, pathname));
  const moreBadge = nav.filter((n) => !tabs.includes(n)).reduce((s, n) => s + badgeOf(n.to), 0);
  const item = (on: boolean) =>
    `relative flex h-full flex-col items-center justify-center gap-0.5 whitespace-nowrap text-[11px] font-semibold ${on ? 'text-brand-700' : 'text-ink-faint'}`;
  return (
    <nav aria-label="아래 탭"
         className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
      <ul className="flex h-14 items-stretch">
        {tabs.map((t) => {
          const b = badgeOf(t.to);
          return (
            <li key={t.to} className="flex-1">
              <NavLink to={t.to} end={t.end} className={item(isOn(t, pathname))}>
                <TabIcon to={t.to} />
                <span>{TAB_LABEL[t.to] ?? t.label}</span>
                {b > 0 && <span className="absolute right-[22%] top-1.5 h-2 w-2 rounded-full bg-red-500" />}
              </NavLink>
            </li>
          );
        })}
        <li className="flex-1">
          <button type="button" onClick={onMore} className={`w-full ${item(!inTabs)}`}>
            <TabIcon to="more" />
            <span>더보기</span>
            {moreBadge > 0 && <span className="absolute right-[22%] top-1.5 h-2 w-2 rounded-full bg-red-500" />}
          </button>
        </li>
      </ul>
    </nav>
  );
}
const TAB_LABEL: Record<string, string> = { '/': '홈', '/dispatch': '배차' };
function TabIcon({ to }: { to: string }) {
  const p: Record<string, JSX.Element> = {
    '/': <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
    '/settle': <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    '/result-daily': <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
    '/dispatch': <><rect x="3" y="5" width="18" height="12" rx="2" /><path d="M3 11h18" /><circle cx="7.5" cy="19" r="1.5" /><circle cx="16.5" cy="19" r="1.5" /></>,
    '/accounting': <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M7 15h4" /></>,
    '/master': <><path d="M4 6h16M4 12h16M4 18h10" /></>,
    '/improve': <path d="M21 12a8 8 0 1 1-3.2-6.4L21 4l-1.4 4.2A8 8 0 0 1 21 12z" />,
    more: <><circle cx="6" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="18" cy="12" r="1.5" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {p[to] ?? p.more}
    </svg>
  );
}

function Splash({ text }: { text: string }) {
  return <div className="grid min-h-full place-items-center text-sm text-ink-soft">{text}</div>;
}

function NotConfigured() {
  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="card max-w-lg p-6">
        <h1 className="text-lg font-bold">Firebase 설정이 없습니다</h1>
        <p className="mt-2 text-sm text-ink-soft">
          <code className="rounded bg-canvas px-1">apps/web/.env.local</code> 이 없거나 값이 비어 있습니다.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-canvas p-3 text-xs">
          cp apps/web/.env.example apps/web/.env.local
        </pre>
        <p className="mt-3 text-sm text-ink-soft">
          아직 GCP 프로젝트 <code className="rounded bg-canvas px-1">businweb</code> 에 Firebase 가 연결되지
          않았습니다. 연결 후 웹앱을 등록하고 키를 채워야 로그인이 동작합니다.
        </p>
      </div>
    </div>
  );
}
